import { useEffect, useState } from 'react';
import { Alert, App, Button, Card, Checkbox, Col, Input, InputNumber, Row, Space, Switch, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';
import { formatWhen } from '../listeners/listenerStats';
import {
  IMAGE_TYPE_OPTIONS,
  minuteToTime,
  settingsBody,
  settingsError,
  timeToMinute,
  type BannerSettings,
  type BannerSettingsView,
} from './welcomeRules';

export const SETTINGS_KEY = ['admin', 'welcome-rules', 'settings'];

function errMsg(e: unknown, fallback: string) {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <Typography.Text strong style={{ display: 'block' }}>
        {label}
      </Typography.Text>
      {children}
      {hint && (
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
          {hint}
        </Typography.Text>
      )}
    </div>
  );
}

/**
 * Banner settings — how often the banner shows, when the day resets, what
 * counts as "opening the app", the default icon and the image limits.
 * Saved as one document; takes effect on each listener's next app open.
 */
export function WelcomeRulesSettingsCard({ settings }: { settings: BannerSettingsView | undefined }) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const canSave = usePermissions().can('welcome_rules.settings');
  const [form, setForm] = useState<BannerSettings | null>(null);

  // (Re)load the form whenever the server copy changes.
  useEffect(() => {
    if (settings) setForm(settingsBody(settings));
  }, [settings]);

  const save = useMutation({
    mutationFn: (body: BannerSettings) => api.put('/admin/welcome-rules/settings', body),
    onSuccess: () => {
      message.success('Banner settings saved');
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
    onError: (e) => message.error(errMsg(e, 'Could not save the settings')),
  });

  if (!form || !settings) return <Card title="Banner settings" loading />;

  const set = (patch: Partial<BannerSettings>) => setForm({ ...form, ...patch });
  const problem = settingsError(form);
  const dirty = JSON.stringify(settingsBody(form)) !== JSON.stringify(settingsBody(settings));
  const off = !canSave;

  return (
    <Card
      title="Banner settings"
      extra={
        canSave && (
          <Button
            type="primary"
            disabled={!dirty || problem !== null}
            loading={save.isPending}
            onClick={() => save.mutate(settingsBody(form))}
          >
            Save settings
          </Button>
        )
      }
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {!form.enabled && <Alert type="warning" showIcon message="The banner is switched off — no listener will see it." />}
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12} xl={8}>
            <Field label="Banner" hint="Master switch for every listener.">
              <Space>
                <Switch aria-label="Banner enabled" checked={form.enabled} disabled={off} onChange={(v) => set({ enabled: v })} />
                <span>{form.enabled ? 'On' : 'Off'}</span>
              </Space>
            </Field>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Field label="Displays per listener per day" hint="Counted on the server, per listener, across all their devices.">
              <InputNumber
                aria-label="Displays per day"
                min={1}
                max={50}
                precision={0}
                value={form.dailyLimit}
                disabled={off}
                onChange={(v) => v !== null && set({ dailyLimit: v })}
              />
            </Field>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Field
              label="Daily reset time"
              hint={`The count starts again at this time. Next reset: ${formatWhen(settings.resetsAt)}.`}
            >
              <Input
                aria-label="Daily reset time"
                type="time"
                style={{ width: 140 }}
                value={minuteToTime(form.dayStartMinute)}
                disabled={off}
                onChange={(e) => {
                  const minute = timeToMinute(e.target.value);
                  if (minute !== null) set({ dayStartMinute: minute });
                }}
              />
            </Field>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Field
              label="Timezone"
              hint={`Leave empty to use the app business timezone. In effect now: ${settings.effectiveTimezone}.`}
            >
              <Input
                aria-label="Timezone"
                placeholder="e.g. Asia/Kolkata"
                maxLength={60}
                value={form.timezone}
                disabled={off}
                onChange={(e) => set({ timezone: e.target.value })}
              />
            </Field>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Field label="Show when the app is launched" hint="App start or login reaching the listener home.">
              <Switch aria-label="Show on launch" checked={form.showOnLaunch} disabled={off} onChange={(v) => set({ showOnLaunch: v })} />
            </Field>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Field
              label="Show when the listener returns to the app"
              hint="Only after the app was in the background for at least this many minutes (0 = every return)."
            >
              <Space>
                <Switch aria-label="Show on return" checked={form.showOnResume} disabled={off} onChange={(v) => set({ showOnResume: v })} />
                <InputNumber
                  aria-label="Background minutes"
                  min={0}
                  max={1440}
                  precision={0}
                  addonAfter="min"
                  style={{ width: 140 }}
                  value={form.reopenAfterMinutes}
                  disabled={off || !form.showOnResume}
                  onChange={(v) => v !== null && set({ reopenAfterMinutes: v })}
                />
              </Space>
            </Field>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Field
              label="Count a skipped banner"
              hint="If a call starts ringing just as the banner is due, the banner is skipped. On: that still uses up one display. Off: the display is given back."
            >
              <Switch
                aria-label="Count skipped banner"
                checked={form.countSkippedDisplay}
                disabled={off}
                onChange={(v) => set({ countSkippedDisplay: v })}
              />
            </Field>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Field label="Default rule icon" hint="Emoji for rules with no icon or image. Empty = a tick.">
              <Input
                aria-label="Default rule icon"
                placeholder="e.g. ✅"
                maxLength={16}
                style={{ width: 140 }}
                value={form.defaultIcon}
                disabled={off}
                onChange={(e) => set({ defaultIcon: e.target.value })}
              />
            </Field>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Field label="Rule image uploads" hint="Largest file and the formats an admin may upload.">
              <Space direction="vertical" size={6}>
                <InputNumber
                  aria-label="Max image size"
                  min={50}
                  max={5120}
                  precision={0}
                  addonAfter="KB"
                  style={{ width: 160 }}
                  value={form.maxImageKb}
                  disabled={off}
                  onChange={(v) => v !== null && set({ maxImageKb: v })}
                />
                <Checkbox.Group
                  options={IMAGE_TYPE_OPTIONS}
                  value={form.imageTypes}
                  disabled={off}
                  onChange={(v) => set({ imageTypes: v })}
                />
              </Space>
            </Field>
          </Col>
        </Row>
        {problem && <Typography.Text type="danger">{problem}</Typography.Text>}
      </Space>
    </Card>
  );
}
